// #region imports

// --- Angular
import { Injectable, OnDestroy } from '@angular/core';

// --- RxJs
import { BehaviorSubject, Observable, ReplaySubject, Subscription } from 'rxjs';

// --- HS Code
import {
  Constants,
  SortDirections,
  SurveyIdentifiersModel,
  SurveyModel,
  SurveySummaryModel,
} from '../../../models';
import { ParticipantGroupsBaseService } from './participant-groups-base.service';
import { AppService } from '../app.service';
import { ResponseBaseService } from './response-base.service';
import { ResultsBaseService } from '../results/results-base.service';
import { UserNotificationBaseService } from '../notifications/user-notification-base.service';
import { RespondentsBaseService } from './respondents-base.service';

// #endregion

@Injectable({
  providedIn: 'root',
})
export abstract class SurveyBaseService implements OnDestroy {
  private selectedSurveysSubscription: Subscription = Subscription.EMPTY;

  protected _selfSurveys$: BehaviorSubject<SurveySummaryModel[] | null>;
  get selfSurveys$(): Observable<SurveySummaryModel[] | null> {
    return this._selfSurveys$;
  }

  protected _selectedSelfSurvey$: BehaviorSubject<SurveySummaryModel | null>;
  get selectedSelfSurvey$(): Observable<SurveySummaryModel | null> {
    return this._selectedSelfSurvey$;
  }

  protected _isLoaded: boolean = false;

  protected _activeSurvey$: BehaviorSubject<SurveyModel | null>;
  get activeSurvey$(): Observable<SurveyModel | null> {
    return this._activeSurvey$;
  }

  protected _isAdding$: BehaviorSubject<boolean>;
  get isAdding$(): Observable<boolean> {
    return this._isAdding$;
  }

  protected _hasReport$: BehaviorSubject<boolean>;
  get hasReport$(): Observable<boolean> {
    return this._hasReport$;
  }

  protected _isLocked$: BehaviorSubject<boolean>;
  get isLocked$(): Observable<boolean> {
    return this._isLocked$;
  }

  protected _surveyError$: BehaviorSubject<string | null>;
  get surveyError$(): Observable<string | null> {
    return this._surveyError$;
  }

  protected _mostRecentSurvey: SurveyIdentifiersModel | null | undefined;
  protected _currentSurveyIdentifiers$: ReplaySubject<SurveyIdentifiersModel | null> = new ReplaySubject<SurveyIdentifiersModel | null>(1);
  get currentSurveyIdentifiers$(): Observable<SurveyIdentifiersModel | null> {
    return this._currentSurveyIdentifiers$.asObservable();
  }

  constructor(
    protected participantGroupsService: ParticipantGroupsBaseService,
    protected appService: AppService,
    protected responseService: ResponseBaseService,
    protected resultsService: ResultsBaseService,
    protected notificationService: UserNotificationBaseService,
    protected respondentsService: RespondentsBaseService
  ) {
    this._activeSurvey$ = new BehaviorSubject<SurveyModel | null>(null);
    this._selfSurveys$ = new BehaviorSubject<SurveySummaryModel[] | null>(
      new Array<SurveySummaryModel>()
    );
    this._selectedSelfSurvey$ = new BehaviorSubject<SurveySummaryModel | null>(
      null
    );
    this._isAdding$ = new BehaviorSubject<boolean>(false);
    this._hasReport$ = new BehaviorSubject<boolean>(false);
    this._isLocked$ = new BehaviorSubject<boolean>(false);
    this._surveyError$ = new BehaviorSubject<string | null>(null);

    this.responseService.activeResponse$.subscribe((response) => {
      if (!response || !this._selectedSelfSurvey$.value) {
        return;
      }

      const selectedSurvey = this._selectedSelfSurvey$.value;
      if (selectedSurvey) {
        selectedSurvey.status = response.status;
        selectedSurvey.updated = new Date();
      }
      this._selectedSelfSurvey$.next(selectedSurvey);
      this._hasReport$.next(selectedSurvey.hasReport);
    });

    this.responseService.surveyResponseProgress$.subscribe((progress) => {
      if (!progress) {
        return;
      }

      const selectedSurvey = this._selectedSelfSurvey$.value;
      if (selectedSurvey) {
        selectedSurvey.percentageComplete = progress;
        selectedSurvey.updated = new Date();
        this._hasReport$.next(selectedSurvey.hasReport);
        this._isLocked$.next(selectedSurvey.isLocked);
      }
      this._selectedSelfSurvey$.next(selectedSurvey);
    });
  }

  ngOnDestroy(): void {
    if (this.selectedSurveysSubscription) {
      this.selectedSurveysSubscription.unsubscribe();
      this.selectedSurveysSubscription = Subscription.EMPTY;
    }
  }

  abstract loadSummaries(
    pageSize: number,
    pageNumber: number,
    filter: string,
    sortItem: string,
    sortDirection: SortDirections,
    forceRefresh?: boolean
  ): void;
  abstract loadSurveyById(id: string): void;
  abstract mostRecentParticipantSurvey(): Observable<SurveyIdentifiersModel | null>;
  abstract getMostRecentParticipantSurvey(ignoreSavedSelectedSurvey?: boolean): void;
  abstract getSurveySummary(id: string): void;

  selectSelfSurvey(surveyIdentifier: string): void {
    const summaries = this._selfSurveys$.value;
    const selectedSurvey = this._selectedSelfSurvey$.value;

    if (!summaries) {
      if (!this.selectedSurveysSubscription) {
        this.selectedSurveysSubscription = this.selfSurveys$.subscribe(
          (qlSummaries: SurveySummaryModel[] | null) => {
            if (qlSummaries && selectedSurvey) {
              this._setSelectedSurvey(
                surveyIdentifier,
                qlSummaries,
                selectedSurvey
              );
            }
          }
        );
      }

      this.loadSummaries(Constants.MAXIMUM_NUMBER_OF_SELF_SURVEYS, 1, '', 'name', SortDirections.descending);

      return;
    }

    if (selectedSurvey) {
      this._setSelectedSurvey(surveyIdentifier, summaries, selectedSurvey);
    }
  }

  getSurveyId(identifier: string): string {
    if (!identifier || !this._selfSurveys$.value) {
      return '';
    }
    const survey = this._selfSurveys$.value.find(
      (s) => s.moniker === identifier || s.id === identifier
    );
    if (!survey) {
      return '';
    }
    return survey.id;
  }

  clear() {
    this._selfSurveys$.next(new Array<SurveySummaryModel>());
  }

  private _setSelectedSurvey(
    surveyIdentifier: string,
    summaries: SurveySummaryModel[],
    selectedSurvey: SurveySummaryModel
  ): void {
    if (summaries && summaries.length) {
      const summary = summaries.find(
        (s) => s.moniker === surveyIdentifier || s.id === surveyIdentifier
      );
      if (summary) {
        this._selectedSelfSurvey$.next(summary);
        localStorage.setItem('selectedSurvey', summary.id);
        this._hasReport$.next(summary.hasReport);
        this.responseService.loadResponse(summary && summary.moniker);
        this.resultsService.loadSurveyResults(summary.moniker);
        if (
          !selectedSurvey ||
          (selectedSurvey.moniker !== surveyIdentifier &&
            selectedSurvey.id !== surveyIdentifier)
        ) {
          this.participantGroupsService.loadGroups(summary.id);
          this.respondentsService.getPreviousRespondents();
        }
      }
    }
  }
}
